import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { FIELD_TYPES, VDValues, VerificationDataRequirements, VerificationDataItem } from 'ng-voxloud';

import { environment } from 'src/environments/environment';
import { AddressService } from 'src/app/shared/services/address/address.service';
import { ErrorToken, createCustomErrorContext } from 'src/app/core/interceptors/generic-error-handling-interceptors';

@Injectable()
export class VerificationDataService {
  constructor(
    private http: HttpClient,
    private addressService: AddressService
  ) { }

  getVerificationDataByCompanyId(company_id: any) {
    let params = new HttpParams({ fromObject: { companyId: company_id } });
    return this.http.get(`${environment.apiEndpoint}/api/v1/verification-data`, { params });
  }

  getVerificationDataById(id: number) {
    return this.http.get(`${environment.apiEndpoint}/api/v1/verification-data/${id}`);
  }

  deleteVerificationData(id: number, errorToken?: ErrorToken[]) {
    return this.http.delete(
      `${environment.apiEndpoint}/api/v1/verification-data/${id}`,
      errorToken ? { context: createCustomErrorContext(errorToken) } : undefined
    );
  }

  createVerificationData(data: FormData) {
    return this.http.post(`${environment.apiEndpoint}/api/v1/verification-data`, data);
  }

  linkVerificationData(didId: number, verificationDataId: number) {
    return this.http.post(`${environment.apiEndpoint}/api/v1/dids/${didId}/verification-data/${verificationDataId}/link`, null)
  }

  getVerificationDataRequirementsByCountry(country: string) {
    return this.http.get(
      `${environment.apiEndpoint}/api/v1/verification-data/requirements/${country.toUpperCase()}`
    );
  }

  buildVerificationData(
    companyId: number,
    country: string,
    requirements: {
      type: string,
      data: VerificationDataRequirements,
      value: VDValues
    }): FormData {
    let result = {
      company_id: companyId,
      country_code: country.toUpperCase(),
      type: requirements.type
    };
    let documents = [];
    let formData: FormData = new FormData();
    const DOCUMENT_FIELDS = ['document_number', 'issuing_authority', 'issuing_date'];

    requirements.data[requirements.type].forEach((el: VerificationDataItem) => {
      switch (el.field_type) {
        case FIELD_TYPES.FULL_ADDRESS:
          result[el.name] = this.addressService.getStructuredData(requirements.value.value[el.name]);
          break;
        case FIELD_TYPES.VAT:
          result[el.name] = requirements.value.value[el.name] =
            `${requirements.value.value[el.name].country}${requirements.value.value[el.name].vat}`;
          break;
        case FIELD_TYPES.DOCUMENT:
          const name = requirements.value.value[el.name].file.name;
          const extension = name.substr(name.lastIndexOf('.'));
          const file_name = `${requirements.value.value[el.name].type}${extension}`;
          let doc = {
            type: requirements.value.value[el.name].type,
            file_name
          };
          // attach document-related fields to corresponding document file
          if ((requirements.type === 'PRIVATE' && doc.type === 'PASSPORT') ||
            (requirements.type === 'BUSINESS' && doc.type === 'COMPANY_REGISTRATION')) {
            let relatedFields: VDValues = {};
            DOCUMENT_FIELDS.forEach(f => {
              relatedFields[f] = requirements.value.value[f] || '-';
            });

            doc = {
              ...doc,
              ...relatedFields
            };
          }
          documents.push(doc);

          formData.append(
            'documents',
            requirements.value.value[el.name].file,
            file_name
          );
          break;
        default:
          result[el.name] = requirements.value.value[el.name];
      }
    });
    if (documents.length > 0) result['documents'] = documents;
    const blob = new Blob([JSON.stringify(result)], { type: 'application/json' });
    formData.append('verification_data', blob);
    return formData;
  }
}
